import {
    AssessmentSchedule,
    ResourceAssessmentScheduleAssociation
} from "../../../../models";
import {
    NotificationResourceType,
    NotificationType,
    ResourceNotificationSetting
} from "../../../../API";
import {
    UseQueryResult,
    useQueries,
    useQuery
} from "react-query";
import {
    useEffect,
    useMemo,
    useState
} from "react";

import { Resource } from "../../../../componentsV2/permission/type/Resource";
import _ from "lodash";
import { graphQLAssessmentScheduleDAO } from "../GraphQLAssessmentScheduleDAO";
import { graphQLResourceAssessmentScheduleAssociationDAO } from "../GraphQLResourceAssessmentScheduleAssociationDAO";
import { graphQLResourceNotificationSettingDAO } from "../../../notification/settings/dao/GraphQLResourceNotificationSettingDAO";

export const useFetchAssessmentSchedules = (propertyId: string, resources: Array<Resource>, enabled: boolean = true) => {
    const [resourceAssessmentScheduleAssociations, setResourceAssessmentScheduleAssociations] = useState<Array<ResourceAssessmentScheduleAssociation> | undefined>(undefined);
    const [missedAssessmentNotificationSettings, setMissedAssessmentNotificationSettings] = useState<Array<ResourceNotificationSetting> | undefined>(undefined);

    const listScheduleAssociationByResourceQueryResults = useQueries(
        resources.map((resource) => {
            return {
                queryKey: ["listAssessmentScheduleAssociationsByResource", resource.resourceId],
                queryFn: async () => {
                    return await graphQLResourceAssessmentScheduleAssociationDAO.listByResource(resource.resourceId, resource.resourceType);
                },
                enabled: enabled && !!resources,
                staleTime: 30000
            };
        })
    );

    const assessmentSchedulesByPropertyQueryResults = useQuery({
        queryKey: ["listAssessmentSchedulesByProperty", propertyId],
        queryFn: async () => {
            return await graphQLAssessmentScheduleDAO.listByProperty(propertyId);
        },
        enabled: enabled && !!propertyId,
        staleTime: 30000
    });

    useEffect(() => {
        setResourceAssessmentScheduleAssociations(prev => {
            if (!listScheduleAssociationByResourceQueryResults.every(queryResult => !!queryResult.data)) {
                return prev;
            }
            const newResourceAssessmentScheduleAssociations = listScheduleAssociationByResourceQueryResults.reduce((acc, queryResult) => {
                if (!queryResult.data) {
                    return acc;
                }
                queryResult.data.forEach((association: ResourceAssessmentScheduleAssociation) => {
                    acc.push(association);
                });
                return acc;
            }, new Array<ResourceAssessmentScheduleAssociation>());
            return _.isEqual(prev, newResourceAssessmentScheduleAssociations) ? prev : newResourceAssessmentScheduleAssociations;
        });
    }, [listScheduleAssociationByResourceQueryResults]);

    const assessmentScheduleIdToResourceIdsMap = useMemo(() => {
        return resourceAssessmentScheduleAssociations?.reduce((acc, association) => {
            if (!association.assessmentScheduleId || !association.resourceId) {
                return acc;
            }
            if (acc.has(association.assessmentScheduleId)) {
                acc.set(association.assessmentScheduleId, [...acc.get(association.assessmentScheduleId)!, association.resourceId]);
                return acc;
            }
            acc.set(association.assessmentScheduleId, [association.resourceId]);
            return acc;
        }, new Map<string, Array<string>>());
    }, [resourceAssessmentScheduleAssociations]);


    const missedAssessmentNotificationSettingsQueryResults = useQueries(
        assessmentSchedulesByPropertyQueryResults.data?.items.map((assessmentSchedule) => {
            return {
                queryKey: ["getMissedAssessmentNotificationSetting", assessmentSchedule.id],
                queryFn: async () => {
                    return await graphQLResourceNotificationSettingDAO.get({
                        resourceId: assessmentSchedule.id,
                        resourceType: NotificationResourceType.ASSESSMENT_SCHEDULE,
                        notificationType: NotificationType.MISSED_ASSESSMENT
                    });
                },
                enabled: enabled && !!assessmentSchedulesByPropertyQueryResults.data,
                staleTime: 30000
            };
        }) ?? []
    );

    useEffect(() => {
        setMissedAssessmentNotificationSettings(prev => {
            if (missedAssessmentNotificationSettingsQueryResults.some(queryResult => queryResult.status !== "success")) {
                return prev;
            }
            const newMissedAssessmentNotificationSettings = missedAssessmentNotificationSettingsQueryResults.map(queryResult => queryResult.data).filter(setting => !!setting) as Array<ResourceNotificationSetting>;
            return _.isEqual(prev, newMissedAssessmentNotificationSettings) ? prev : newMissedAssessmentNotificationSettings;
        });
    }, [missedAssessmentNotificationSettingsQueryResults]);

    const status: UseQueryResult["status"] = useMemo(() => {
        if (
            listScheduleAssociationByResourceQueryResults.some((queryResult) => queryResult.status === "error") ||
            assessmentSchedulesByPropertyQueryResults.status === "error" ||
            missedAssessmentNotificationSettingsQueryResults.some((queryResult) => queryResult.status === "error")
        ) {
            return "error";
        }
        if (
            listScheduleAssociationByResourceQueryResults.some((queryResult) => queryResult.status === "loading") ||
            assessmentSchedulesByPropertyQueryResults.status === "loading" ||
            missedAssessmentNotificationSettingsQueryResults.some((queryResult) => queryResult.status === "loading")
        ) {
            return "loading";
        }
        if (
            listScheduleAssociationByResourceQueryResults.some((queryResult) => queryResult.status === "idle") ||
            assessmentSchedulesByPropertyQueryResults.status === "idle" ||
            missedAssessmentNotificationSettingsQueryResults.some((queryResult) => queryResult.status === "idle")
        ) {
            return "idle";
        }
        return "success";
    }, [listScheduleAssociationByResourceQueryResults, assessmentSchedulesByPropertyQueryResults, missedAssessmentNotificationSettingsQueryResults]);

    return {
        resourceAssessmentScheduleAssociations,
        assessmentSchedules: assessmentSchedulesByPropertyQueryResults.data?.items.filter(schedule => !!schedule) as Array<AssessmentSchedule>,
        assessmentScheduleIdToResourceIdsMap,
        status,
        missedAssessmentNotificationSettings
    };
};